/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  ConfigureAndBuy as CAndB,
  ColorSelectorTypes,
  StockStatuses,
  MiniCart,
  AddToCart,
  useIsMobile,
  Dialog as UIFoundationsDialog,
  RatingAndReviewsTypes
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  AttributeSelectorItemProps,
  AttributeSelectorProps
} from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/common/attribute-selector'
import { FlavourProfilesProps } from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/common/flavour-profiles'
import type { ConfigureAndBuyProps } from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/configure-and-buy/types'
import { useCookies } from 'react-cookie'
import type { ColorProps } from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/common/color-selector/types'
import type { ChangeEvent } from 'react'
import { canUseDom, useIsSsr } from '../../../utils/isSsr'
import { UserDataContext, UserTypes } from '../../../services/user/user-data'
import { useLocalStorageCart } from '../../../services/product/use-cart-local-storage'
import {
  GIGYA_COOKIE_JWT,
  GIGYA_COOKIE_UID,
  HYBRIS_ACCESS_TOKEN,
  REPLACE_PREORDER_DATE,
  COLOR_SELECTOR_ID_SUFIX
} from '../../../utils/constants'
import ExperienceFragmentContent from '../../templates/ExperienceFragmentContent/ExperienceFragmentContent'
import {
  setLayoutBottomShift,
  useInitLayoutTopShift
} from '../../../utils/app-layout'
import { setBreadcrumbTitle } from '../../../utils/breadcrumb-context'
import { handleAddToCart } from '../common/cart-management/handleAddToCart'
import CartErrorMessageDialog from '../common/cart-management/CartErrorMessageDialog'
import {
  createDataLayerEcommerceProduct,
  pushDataLayerProductDetailsView
} from '../common/datalayer/product'
import { getOptimizedImageExtension } from '../../../utils/image-extensions'
import { getAssetImageUrl } from '../../../utils/asset-url'
import {
  EditableComponentPropsType,
  GetCAndBHeadDataReturnType
} from '../../../utils/prop-types'
import { generateProductJsonLD } from '../common/product-jsonld'
import { ProductStockData, StockLevelStatus } from '../../../services/stock'
import {
  ConfigureAndBuyModelProps,
  ConfigureAndBuyStaticModelProps,
  AttributeSelectorItem
} from '../common/types'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../utils/aem/editor/extractEditableProps'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../functional/ErrorBoundary'
import {
  checkIsPreorder,
  isValidDate,
  replaceDateFormat
} from '../../../utils/preorder'
import {
  getOrderQuantityValues,
  isMinQuantityExceedsStock
} from '../../../utils/quantity-selector'
import headTagsConfig from './headTagsConfig'
import { getIntensityGauge } from '../common/intensity-gauge'
import {
  createAddToCartMethodConfig,
  getAddToCartButtonLabel,
  redirectToLoginPage,
  useMiniCartConfig
} from '../common/cart-management/cartConfig'
import type { CreateAddToCartMethodConfigProps } from '../common/cart-management/cartConfig'
import {
  getStockInfoLabel,
  getEffectiveStockLevelStatus
} from '../common/stock-info'
import { setMarkerProductData } from '../../../utils/marker-product-data'
import Container from '../../templates/Container/Container'
import { setFormSubmitStatus } from '../../../utils/handle-form-submit'
import {
  fetchContainerContent,
  setDialogConfig
} from '../common/dialog-configuration'
import { formatDateSimple } from '../../../utils/date-format'
import {
  fetchProductData,
  fetchStockData,
  getProductFromData
} from '../../../services/product/productService'
import { getProductImageGalleryProps } from '../common/product-image-gallery'
import {
  ProductDataVariant,
  VariantWithDynamicAttributes
} from '../../../services/product/product-data'
import { getProductDescription } from '../common/product-description'
import { PageConfig, type AppConfig } from '../../../utils/page-config'
import {
  getPreviewHeaders,
  getServerSideHeaders
} from '../../../utils/api-headers'
import {
  findProduct,
  getAttributesSelectorProps,
  getColorSelectorProperties,
  handleAttributeSelectorChange
} from '../common/variant-selector'
import { getDeliveryMessage } from '../common/delivery-message'
import { getPriceInfo } from '../common/price-info'
import {
  getMultibuyAttributeSelectorProps,
  getMultibuyDefaultQty,
  getMultibuyHelperText,
  getMultibuySelectProps
} from '../common/multibuy'
import { formatPrice, FormatPriceOptions } from '../../../utils/format-price'
import { setCurrentProductSeoUrl } from '../../../utils/language-switcher-context'

if (canUseDom) {
  // eslint-disable-next-line no-void
  void import('@dcxwam/dcx-wam-ui-foundations/css')
}

const stockStatusMap = {
  inStock: StockStatuses.InStock,
  outOfStock: StockStatuses.OutOfStock,
  lowStock: StockStatuses.LowStock
} as const

type StockStatusKey = keyof typeof stockStatusMap

const getStockStatus = (status?: string): StockStatuses | undefined => {
  if (!status) {
    return undefined
  }
  return stockStatusMap[status as StockStatusKey]
}

const outOfStockValue = 'outOfStock'

// Static variables for ConfigureAndBuy
let setupGcode: string | undefined
const gcodeSetupSubscribers: Record<
  string,
  { action: (gcode?: string) => void }
> = {}

// Static functions for ConfigureAndBuy
const unsubscribeFromGcodeSetup = (id: string) => {
  if (id && gcodeSetupSubscribers[id]) {
    delete gcodeSetupSubscribers[id]
  }
}

const subscribeToGcodeSetup = (
  id: string,
  action: (gcode?: string) => void
) => {
  if (id) {
    unsubscribeFromGcodeSetup(id)
    gcodeSetupSubscribers[id] = { action }
    if (setupGcode) {
      action(setupGcode)
    }
  }
}

const getSortedVariants = (variants?: ProductDataVariant[]) =>
  variants?.sort(
    (variantA: ProductDataVariant, variantB: ProductDataVariant) => {
      if (
        variantA?.sequenceNumber &&
        variantB?.sequenceNumber &&
        variantA?.sequenceNumber !== variantB?.sequenceNumber
      ) {
        return variantA?.sequenceNumber < variantB?.sequenceNumber ? -1 : 1
      }
      const codeA = variantA?.globalArticleCode ?? ''
      const codeB = variantB?.globalArticleCode ?? ''
      return codeA < codeB ? -1 : 1
    }
  ) || []

const ConfigureAndBuyComponent = withComponentRefresh((props) => {
  const {
    attributeSelector = [],
    id,
    key,
    config,
    ratesAndReviews,
    general,
    deliveryMessage,
    product,
    notifyMe,
    imageGallery,
    initialProductData,
    miniCart,
    addToCart,
    productSeoNames,
    flavour,
    productRootPath = '',
    shopConfig,
    ...rest
  } = props.model
  const editableProps = useExtractEditableProps(props, rest)
  const isSsr = useIsSsr()
  const isMobile = useIsMobile()
  const attSelectorMarkers = (
    (
      config as {
        attributeSelectorMarkers?: Array<{ label: string; value: string }>
      }
    )?.attributeSelectorMarkers ?? []
  ).map((m) => ({ value: m.value, label: m.label }))
  // use current user hook before submitting add to cart request
  const userData = useContext(UserDataContext)
  const [caConfig] = useState(
    (PageConfig?.getConfig() || {}) as AppConfig & {
      lang?: string
      locale?: string
    }
  )
  const pageLocale = caConfig?.locale

  const formatPriceOptions: FormatPriceOptions = useMemo(
    () => ({
      currencySymbol: caConfig?.currencyConfig?.currencySymbol,
      fractionDigits: caConfig?.currencyConfig?.fractionDigits,
      priceFormat: caConfig?.currencyConfig?.priceFormat
    }),
    [caConfig?.currencyConfig]
  )

  const [notifyMeContent, setNotifyMeContent] = useState({})

  const quantityLabel = shopConfig?.quantityLabel

  const ERROR_MESSAGE_FROM_CA = shopConfig?.addToCartErrorMessage
  const topShift = useInitLayoutTopShift()
  const [cartLocalStorageJSON, setCartLocalStorage] = useLocalStorageCart()
  const productEntry = cartLocalStorageJSON?.entry
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [isButtonLoading, setIsButtonLoading] = useState(false)
  const [activeMiniCart, setActiveMiniCart] = useState(false)

  const [productData, setProductData] = useState<
    ProductDataVariant | undefined
  >(getProductFromData<ProductDataVariant>(initialProductData))
  // set initial productID
  const [productID, setProductID] = useState(productData?.globalArticleCode)
  const visibleVariants =
    productData?.variants?.filter(
      (variant: ProductDataVariant) =>
        variant?.seo?.productsSeoUrl &&
        productSeoNames?.includes(variant.seo.productsSeoUrl)
    ) ?? []
  const [cookies, setCookie] = useCookies([
    GIGYA_COOKIE_UID,
    HYBRIS_ACCESS_TOKEN,
    GIGYA_COOKIE_JWT
  ])
  const [dialogErrorMessageIsOpen, setDialogErrorMessageIsOpen] =
    useState(false)
  const [dialogNotifyMeIsOpen, setDialogNotifyMeIsOpen] = useState(false)
  const [errorMessageContent, setErrorMessageContent] = useState({
    message: null,
    title: ERROR_MESSAGE_FROM_CA,
    actions: null
  })
  const [productQuantity, setProductQuantity] = useState(
    (
      productData?.variants?.find(
        (v: ProductDataVariant) =>
          productData?.globalArticleCode === v?.globalArticleCode
      ) as ProductDataVariant
    )?.minimumOrderQuantity || 1
  )

  const [selectedMultibuyQty, setSelectedMultibuyQty] = useState<number>(1)

  const onMultibuyChange = useCallback((qty: number) => {
    setSelectedMultibuyQty(qty)
    setProductQuantity(qty)
  }, [])

  const productsMapByGCode = new Map(
    productData?.variants
      ?.filter((v) => v?.globalArticleCode)
      ?.map((variant) => [variant.globalArticleCode!, variant]) ?? []
  )

  const [currentProductStockMap, setCurrentProductStockMap] = useState(
    new Map(
      productData?.variants
        ?.filter((v) => v?.globalArticleCode)
        ?.map((productStock) => [
          productStock.globalArticleCode!,
          productStock as ProductStockData
        ]) ?? []
    )
  )

  const onAddToCartQuantityChange = useCallback(
    (event: ChangeEvent<EventTarget>) => {
      setProductQuantity(Number((event.target as HTMLInputElement).value))
    },
    []
  )

  const mobileStickyCtaId = `${id}-sticky-cta`

  const onStickyColorNameClick = useCallback(() => {
    const targetEl = document.getElementById(`${id}-${COLOR_SELECTOR_ID_SUFIX}`)
    const scrollPosition =
      (targetEl?.getBoundingClientRect().top || 0) + window.scrollY - topShift
    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    })
  }, [id, topShift])

  const onStickyAddToCartDisplayChange = useCallback(
    (isStickyVisible: boolean) => {
      setLayoutBottomShift(
        isStickyVisible && isMobile
          ? document.getElementById(mobileStickyCtaId)?.offsetHeight || 0
          : 0
      )
    },
    [isMobile, mobileStickyCtaId]
  )

  const fetchSData = async (productsMap: Map<string, ProductDataVariant>) => {
    const cbxConfigResolved = config?.cbxConfig ?? {
      cbxUrl: config?.cbxUrl,
      market: config?.market,
      channel: config?.channel,
      brand: config?.brand,
      locale: config?.locale,
      xChannel: config?.xChannel,
      loggerEnabled: config?.loggerEnabled
    }
    const customHeaders = getPreviewHeaders()

    const articleList = Array.from(productsMap.keys()).filter(
      (articleKey): articleKey is string => Boolean(articleKey)
    )

    const stockData = general?.ignoreStock
      ? articleList.map((code) => ({
          code,
          stock: {
            stockLevelStatus: StockLevelStatus.inStock,
            stockLevel: Number.MAX_SAFE_INTEGER
          }
        }))
      : await fetchStockData({
          config: cbxConfigResolved,
          articleList,
          customHeaders
        })

    if (stockData?.length) {
      const productStockMap = new Map(
        stockData?.map((productStock) => [productStock.code, productStock])
      )
      setCurrentProductStockMap(productStockMap)
    }
  }

  const [variants, setVariants] = useState(getSortedVariants(visibleVariants))
  const [currentAttributeSelectedValues, setCurrentAttributeSelectedValues] =
    useState({})

  const currentProductVariant = useMemo(
    () => ({
      productFamily: productData?.productFamily,
      ...(variants?.find(
        (v: ProductDataVariant) => productID === v?.globalArticleCode
      ) || {})
    }),
    [productData?.productFamily, variants, productID]
  )

  const attributeSelectorMap = useMemo(
    () =>
      attributeSelector?.reduce(
        (acc: Record<string, string>, attr: AttributeSelectorItem) => ({
          ...acc,
          [attr.name]: attr.label
        }),
        {} as Record<string, string>
      ) ?? {},
    [attributeSelector]
  )

  const attributeNames = useMemo(
    () => Object.keys(attributeSelectorMap) || [],
    [attributeSelectorMap]
  )
  const attributeNameOptions = useMemo(
    () =>
      variants?.reduce(
        (acc: Record<string, string[]>, variant: ProductDataVariant) => {
          const newAttributesAcc = { ...acc }
          attributeNames.forEach((attributeName) => {
            const options = acc[attributeName] || []
            const variantValue = (variant as VariantWithDynamicAttributes)[
              attributeName
            ]
            if (
              typeof variantValue === 'string' &&
              !options.includes(variantValue)
            ) {
              options.push(variantValue)
            }
            newAttributesAcc[attributeName] = options
          })
          return newAttributesAcc
        },
        {} as Record<string, string[]>
      ) || {},
    [variants]
  )

  const updateGcodeSetupSubscribers = useCallback(() => {
    Object.values(gcodeSetupSubscribers)?.forEach((subscriber) => {
      subscriber?.action?.(productID)
    })
  }, [gcodeSetupSubscribers, productID])

  const onVariantChange = useCallback(
    (clickedVariant: {
      globalArticleCode?: string
      seo?: { productsSeoUrl?: string }
      nuance?: { code?: string }
    }) => {
      if (!clickedVariant?.seo?.productsSeoUrl) {
        return
      }

      setCurrentProductSeoUrl(clickedVariant.seo.productsSeoUrl)

      const queryString = window.location.search
      window.history.replaceState(
        null,
        '',
        encodeURIComponent(`${clickedVariant.seo.productsSeoUrl}.html`) +
          queryString
      )
      fetchSData(productsMapByGCode)
    },
    [productsMapByGCode]
  )

  const onAttributeSelectorChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      attributeOption: AttributeSelectorItemProps
    ) => {
      e?.preventDefault()
      attributeNames.forEach((attributeName) => {
        const { clickedVariant, newProductAttributeSelectedValues } =
          handleAttributeSelectorChange({
            attributeOption,
            config: general || {},
            productVariants: variants,
            attributeNames,
            currentAttributeSelectedValues,
            attributeName,
            currentProductVariant
          })

        if (
          clickedVariant?.globalArticleCode &&
          clickedVariant.globalArticleCode !== productID
        ) {
          setCurrentAttributeSelectedValues(newProductAttributeSelectedValues)
          setProductID(clickedVariant.globalArticleCode)
          onVariantChange(clickedVariant)
        }

        return null
      })
    },
    [
      attributeNames,
      general,
      variants,
      currentAttributeSelectedValues,
      currentProductVariant
    ]
  )

  const attributesSelectorProps: AttributeSelectorProps[] | undefined = useMemo(
    () =>
      getAttributesSelectorProps({
        attributeNames,
        attributeSelectorMap,
        currentAttributeSelectedValues,
        attributeNameOptions,
        markers: attSelectorMarkers,
        generalConfig: general,
        currentProductVariant,
        variants,
        attributeSelector,
        onChange: onAttributeSelectorChange,
        pageLocale,
        currentProductStockMap,
        formatPriceOptions
      }),
    [
      attributeNames,
      attributeSelectorMap,
      currentAttributeSelectedValues,
      attributeNameOptions,
      attSelectorMarkers,
      general,
      currentProductVariant,
      variants,
      attributeSelector,
      onAttributeSelectorChange,
      pageLocale,
      currentProductStockMap
    ]
  )

  const shouldShowAttributeSelector = useMemo(
    () =>
      attributesSelectorProps?.some(
        (attrProp: AttributeSelectorProps) =>
          Array.isArray(attrProp?.attributeVariants) &&
          attrProp?.attributeVariants?.length > 1
      ),
    [attributesSelectorProps]
  )

  const colorName = currentProductVariant?.nuance?.description

  const currentProductVariantStockLevelStatus = currentProductStockMap.get(
    currentProductVariant?.globalArticleCode ?? ''
  )?.stock?.stockLevelStatus

  const stockLevelFromMap = currentProductStockMap.get(
    currentProductVariant?.globalArticleCode ?? ''
  )?.stock?.stockLevel

  // Multibuy integration
  const multiBuyPrice = currentProductVariant?.price?.multiBuyPrice
  const isVariantOutOfStock = useMemo(
    () =>
      currentProductVariantStockLevelStatus === outOfStockValue ||
      currentProductVariant?.stock?.stockLevelStatus === outOfStockValue ||
      isMinQuantityExceedsStock(
        currentProductVariant?.minimumOrderQuantity,
        stockLevelFromMap
      ),
    [
      currentProductVariantStockLevelStatus,
      currentProductVariant?.stock?.stockLevelStatus,
      currentProductVariant?.minimumOrderQuantity,
      stockLevelFromMap
    ]
  )
  const isMultibuyActive = Boolean(
    shopConfig?.multibuyEnabled && multiBuyPrice?.length
  )

  const multibuyDefaultQty = useMemo(() => {
    if (!isMultibuyActive || !multiBuyPrice?.length) {
      return 1
    }
    return getMultibuyDefaultQty({
      multiBuyPrice,
      bestValueToggle: shopConfig?.multibuyBestValueToggle,
      stockLevel: stockLevelFromMap
    })
  }, [
    isMultibuyActive,
    multiBuyPrice,
    shopConfig?.multibuyBestValueToggle,
    stockLevelFromMap
  ])

  useEffect(() => {
    if (isMultibuyActive && multibuyDefaultQty > 0) {
      setSelectedMultibuyQty(multibuyDefaultQty)
      setProductQuantity(multibuyDefaultQty)
    }
  }, [multibuyDefaultQty, isMultibuyActive])

  useEffect(() => {
    if (!isMultibuyActive && currentProductVariant?.minimumOrderQuantity) {
      setProductQuantity(currentProductVariant.minimumOrderQuantity)
    }
  }, [isMultibuyActive, currentProductVariant?.minimumOrderQuantity, productID])

  const multibuyAttributeSelectorProps = useMemo(() => {
    if (!isMultibuyActive || !multiBuyPrice?.length || !shopConfig) {
      return undefined
    }
    return getMultibuyAttributeSelectorProps({
      multiBuyPrice,
      shopConfig,
      categoryIdentifier: currentProductVariant?.categoryIdentifier,
      unitOfMeasure: currentProductVariant?.unitOfMeasure,
      priceCurrency: currentProductVariant?.price?.priceCurrency,
      pageLocale: pageLocale ?? '',
      selectedQty: selectedMultibuyQty,
      onChange: onMultibuyChange,
      isDisabled: isVariantOutOfStock,
      stockLevel: stockLevelFromMap,
      formatPriceOptions
    })
  }, [
    isMultibuyActive,
    multiBuyPrice,
    shopConfig,
    currentProductVariant?.categoryIdentifier,
    currentProductVariant?.unitOfMeasure,
    currentProductVariant?.price?.priceCurrency,
    pageLocale,
    selectedMultibuyQty,
    onMultibuyChange,
    isVariantOutOfStock,
    stockLevelFromMap
  ])

  const multibuySelectProps = useMemo(() => {
    if (!isMultibuyActive || !multiBuyPrice?.length || !shopConfig) {
      return undefined
    }
    return getMultibuySelectProps({
      multiBuyPrice,
      shopConfig,
      categoryIdentifier: currentProductVariant?.categoryIdentifier,
      unitOfMeasure: currentProductVariant?.unitOfMeasure,
      priceCurrency: currentProductVariant?.price?.priceCurrency,
      pageLocale: pageLocale ?? '',
      selectedQty: selectedMultibuyQty,
      onChange: onMultibuyChange,
      isDisabled: isVariantOutOfStock,
      stockLevel: stockLevelFromMap,
      formatPriceOptions
    })
  }, [
    isMultibuyActive,
    multiBuyPrice,
    shopConfig,
    currentProductVariant?.categoryIdentifier,
    currentProductVariant?.unitOfMeasure,
    currentProductVariant?.price?.priceCurrency,
    pageLocale,
    selectedMultibuyQty,
    onMultibuyChange,
    isVariantOutOfStock,
    stockLevelFromMap
  ])

  const multibuyEditorialDescription = useMemo(() => {
    if (!isMultibuyActive || !multiBuyPrice?.length || !shopConfig) {
      return undefined
    }
    return getMultibuyHelperText({
      multiBuyPrice,
      shopConfig,
      selectedQty: selectedMultibuyQty,
      priceCurrency: currentProductVariant?.price?.priceCurrency,
      pageLocale: pageLocale ?? '',
      formatPriceOptions
    })
  }, [
    isMultibuyActive,
    multiBuyPrice,
    shopConfig,
    selectedMultibuyQty,
    currentProductVariant?.price?.priceCurrency,
    pageLocale
  ])

  const multibuyCurrentPrice = useMemo(() => {
    if (!isMultibuyActive || !multiBuyPrice?.length) {
      return undefined
    }
    const selectedEntry = multiBuyPrice.find(
      (entry) => entry.qty === selectedMultibuyQty
    )
    if (!selectedEntry) {
      return undefined
    }
    return formatPrice({
      value: selectedEntry.totalPrice,
      currency: currentProductVariant?.price?.priceCurrency,
      locale: pageLocale ?? '',
      options: formatPriceOptions
    })
  }, [
    isMultibuyActive,
    multiBuyPrice,
    selectedMultibuyQty,
    currentProductVariant?.price?.priceCurrency,
    pageLocale
  ])

  const dataLayerEcommerceProduct = useMemo(
    () =>
      createDataLayerEcommerceProduct({
        ...currentProductVariant,
        stock: {
          stockLevelStatus: currentProductVariantStockLevelStatus
        }
      }),
    [currentProductVariant, currentProductVariantStockLevelStatus]
  )

  const getAddToCartMethodConfig = useCallback(() => {
    return createAddToCartMethodConfig({
      orderType: general?.orderType ?? '',
      lendingConfigurationCode: (
        general as { lendingConfigurationCode?: string }
      )?.lendingConfigurationCode,
      setIsButtonDisabled,
      setIsButtonLoading,
      dataLayerEcommerceProduct,
      productData: currentProductVariant,
      productID: productID ?? '',
      caConfig: {
        shopConfig,
        hybrisConfig: config
          ? {
              hybrisUrl: config.hybrisUrl,
              hybrisClientId: config.hybrisClientId,
              hybrisClientSecret: config.hybrisClientSecret,
              baseSiteId: config.baseSiteId,
              language: config.language
            }
          : caConfig.hybrisConfig,
        muleSoftConfig: caConfig.muleSoftConfig,
        market: caConfig.market,
        lang: caConfig.lang
      },
      setActiveMiniCart,
      cookies,
      setCookie: setCookie as CreateAddToCartMethodConfigProps['setCookie'],
      cartLocalStorageJSON,
      setCartLocalStorage,
      setErrorMessageContent:
        setErrorMessageContent as CreateAddToCartMethodConfigProps['setErrorMessageContent'],
      setDialogErrorMessageIsOpen,
      userData,
      productQuantity: productQuantity || 1
    })
  }, [
    general?.orderType,
    general?.lendingConfigurationCode,
    dataLayerEcommerceProduct,
    currentProductVariant,
    productID,
    product,
    config,
    caConfig,
    cookies,
    cartLocalStorageJSON,
    userData,
    productQuantity
  ])

  const configMiniCart = useMiniCartConfig({
    productEntry,
    config: {
      ...miniCart,
      showOldPrices: general?.showOldPrices ?? true,
      successMessage: shopConfig?.successMessage ?? '',
      checkoutCTALabel: shopConfig?.checkoutCTALabel ?? '',
      checkoutCTAUrl: shopConfig?.checkoutCTAUrl ?? '',
      cartCTALabel: shopConfig?.cartCTALabel ?? '',
      cartCTAUrl: shopConfig?.cartCTAUrl ?? '',
      closeButtonAriaLabel: shopConfig?.closeButtonAriaLabel ?? ''
    },
    variants: productData?.variants ?? [],
    cartLocalStorageJSON,
    activeMiniCart,
    setActiveMiniCart,
    productQuantityLabel: shopConfig?.quantityLabel ?? ''
  })

  useEffect(() => {
    // Fallback product call on CSR when no server data is given
    if (!initialProductData && config) {
      const fetchPData = async () => {
        if (!config?.cbxUrl || !config?.market || !config?.channel) {
          return
        }
        const customHeaders = getPreviewHeaders()

        const mappedData = await fetchProductData<ProductDataVariant>({
          config,
          key: key ?? '',
          includeVariants: 'true',
          includeRelationships: 'true',
          includeOffline: 'false',
          includeCrosslinked: 'false',
          customHeaders
        })
        if (mappedData) {
          setProductData(mappedData)
        }
      }
      fetchPData()
    }
  }, [])

  useEffect(() => {
    if (productData) {
      setIsButtonDisabled(false)
      setProductID(productData?.globalArticleCode ?? '')
      setVariants(getSortedVariants(visibleVariants ?? []))
      setBreadcrumbTitle(productData?.baseArticle?.baseModelName ?? '')
      fetchSData(productsMapByGCode)
      const productVariant = {
        productFamily: productData?.productFamily,
        ...(productData?.variants?.find(
          (v: ProductDataVariant) =>
            productData?.globalArticleCode === v?.globalArticleCode
        ) || {})
      }
      setCurrentProductSeoUrl(productVariant?.seo?.productsSeoUrl ?? '')
      setProductQuantity(productVariant?.minimumOrderQuantity || 1)
      setCurrentAttributeSelectedValues(
        attributeSelector?.reduce(
          (acc: Record<string, string>, attr: AttributeSelectorItem) => ({
            ...acc,
            [attr.name]: (productVariant as Record<string, unknown>)[
              attr.name
            ] as string
          }),
          {} as Record<string, string>
        )
      )
    }
  }, [productData])

  useEffect(() => {
    if (!isSsr && Object.keys(currentProductVariant).length > 0) {
      pushDataLayerProductDetailsView(dataLayerEcommerceProduct)
    }
    setupGcode = productID
    updateGcodeSetupSubscribers()
  }, [productID, isSsr])

  const deliveryInfoMessage = useMemo(
    () => getDeliveryMessage(deliveryMessage || {}, isSsr),
    [deliveryMessage, isSsr]
  )

  const isGuestAddToCart =
    shopConfig?.guestAddToCartEnabled ||
    userData?.decoratedUserType !== UserTypes.Las

  const isPreorder = checkIsPreorder(currentProductVariant?.preOrderUntilDate)

  const isButtonInDefaultState =
    !isPreorder &&
    currentProductVariantStockLevelStatus &&
    (currentProductVariantStockLevelStatus !== outOfStockValue ||
      (stockLevelFromMap !== undefined && stockLevelFromMap > 0)) &&
    isGuestAddToCart

  const hasMultibuyChoices =
    (multibuyAttributeSelectorProps?.attributeVariants?.length ?? 0) > 0

  const isQTYSelectorVisible =
    isButtonInDefaultState &&
    general?.enableQuantitySelector &&
    !hasMultibuyChoices &&
    !isMinQuantityExceedsStock(
      currentProductVariant?.minimumOrderQuantity,
      stockLevelFromMap
    )

  // handle change of color selector
  const onColorClick = useCallback(
    (e: React.MouseEvent, clickedColor: ColorProps) => {
      e?.preventDefault()
      const clickedVariant = findProduct({
        selectedColorValue: clickedColor.value,
        selectedAttributes: currentAttributeSelectedValues,
        generalProductConfig: general,
        variants,
        attributeNames
      })

      if (
        clickedVariant?.globalArticleCode &&
        clickedVariant.globalArticleCode !== productID
      ) {
        setProductID(clickedVariant.globalArticleCode)
        onVariantChange(clickedVariant)
      }
    },
    [
      currentAttributeSelectedValues,
      general,
      variants,
      attributeNames,
      productID
    ]
  )

  const colorSelectorProperties = useMemo(
    () =>
      getColorSelectorProperties({
        variants,
        currentProductStockMap,
        productRootPath,
        generalConfig: general,
        currentAttributeSelectedValues,
        outOfStockValue,
        attributeNames,
        currentProductVariant,
        colorName: colorName ?? '',
        onChange: onColorClick as (
          e: ChangeEvent<HTMLInputElement> | React.MouseEvent,
          clickedColor: ColorProps
        ) => void,
        type: ColorSelectorTypes.Hyperlink,
        colorGroupAriaLabel: shopConfig?.colorGroupAriaLabel ?? '',
        id: id
          ? `${id}-${COLOR_SELECTOR_ID_SUFIX}`
          : `cab-${COLOR_SELECTOR_ID_SUFIX}`
      }),
    [
      variants,
      currentAttributeSelectedValues,
      currentProductStockMap,
      currentProductVariant
    ]
  )

  const effectiveStockLevelStatus = getEffectiveStockLevelStatus(
    currentProductVariantStockLevelStatus ?? '',
    isMinQuantityExceedsStock(
      currentProductVariant?.minimumOrderQuantity,
      stockLevelFromMap
    )
  )

  const stockInfoLabel = getStockInfoLabel({
    isSsr,
    stockLevelStatus: effectiveStockLevelStatus,
    shopConfig
  })

  const preOrderDate =
    currentProductVariant?.preOrderUntilDate &&
    isValidDate(currentProductVariant?.preOrderUntilDate)
      ? replaceDateFormat(currentProductVariant?.preOrderUntilDate)
      : undefined

  const minExceedsStockLabel = shopConfig?.notifyMe
    ? shopConfig?.notifyMeCTALabel
    : shopConfig?.outOfStockCTALabel

  const addToCartButtonLabel =
    !isPreorder &&
    isMinQuantityExceedsStock(
      currentProductVariant?.minimumOrderQuantity,
      stockLevelFromMap
    )
      ? minExceedsStockLabel
      : getAddToCartButtonLabel({
          guestAddToCartEnabled: shopConfig?.guestAddToCartEnabled,
          preOrderUntilDate: preOrderDate || undefined,
          stockLevelStatus: currentProductVariantStockLevelStatus,
          addToCartLabel: shopConfig?.addToCartLabel,
          preOrderLabel: shopConfig?.preOrderCTALabel,
          notifyMe: shopConfig?.notifyMe,
          notifyMeCTALabel: shopConfig?.notifyMeCTALabel,
          outOfStockLabel: shopConfig?.outOfStockCTALabel,
          loginToBuyLabel: shopConfig?.loginToBuyLabel,
          userData
        })

  const locale =
    caConfig?.lang && caConfig?.market
      ? `${caConfig.lang}-${caConfig.market}`
      : caConfig?.lang

  const formattedPreorderDate = useMemo(
    () => formatDateSimple(preOrderDate || undefined, locale ?? ''),
    [preOrderDate, locale]
  )

  const preOrderMessage =
    shopConfig?.preOrderMessage?.replace(
      REPLACE_PREORDER_DATE,
      formattedPreorderDate ?? ''
    ) || shopConfig?.preOrderMessage

  const priceInfoConfig = useMemo(
    () =>
      getPriceInfo({
        data: currentProductVariant?.price,
        pageLocale: pageLocale ?? '',
        showOldPrices: general?.showOldPrices,
        currentPriceLabelProp: general?.currentPriceLabel,
        oldPriceLabelProp: general?.oldPriceLabel,
        formatPriceOptions
      }),
    [currentProductVariant, pageLocale, general]
  )

  const {
    currentPrice,
    originalPrice,
    currentPriceTextProps,
    originalPriceTextProps
  } = priceInfoConfig ?? {}

  const priceInfoProps = {
    currentPrice,
    originalPrice
  }

  const priceInfoTextProps = {
    currentPriceTextProps,
    originalPriceTextProps
  }

  const orderQTYValues = useMemo(
    () =>
      getOrderQuantityValues(
        currentProductVariant?.minimumOrderQuantity,
        currentProductVariant?.maximumOrderQuantity,
        stockLevelFromMap
      ),
    [
      currentProductVariant?.minimumOrderQuantity,
      currentProductVariant?.maximumOrderQuantity,
      stockLevelFromMap
    ]
  )

  const productDescription = getProductDescription(currentProductVariant)
  const editorialDescription = currentProductVariant?.editorialDescription

  const showFlavourProfiles =
    shopConfig?.flavourBanEnabled &&
    general?.enableFlavourProfile &&
    (currentProductVariant?.intensity ||
      currentProductVariant?.intensityMeter ||
      shopConfig?.intensityLabel ||
      currentProductVariant?.intensityInfo)

  const productImageGalleryProps = useMemo(
    () =>
      getProductImageGalleryProps({
        productVariant: currentProductVariant,
        backupImageUrl: general?.backupImageUrl,
        enableLazyLoad: general?.enableLazyLoad
      }),
    [currentProductVariant, general]
  )
  const showStockInfoLabel =
    (shopConfig?.showInStockInfoEnable &&
      !isSsr &&
      stockInfoLabel === shopConfig?.inStockLabel) ||
    (shopConfig?.showInStockInfoEnable && isSsr) ||
    (stockInfoLabel !== shopConfig?.inStockLabel && !isSsr)
  const shouldShowAddToCart = addToCart?.addToCartCtaVisible

  const onATCButtonClick = useCallback(() => {
    setMarkerProductData(productData)
    if (
      addToCartButtonLabel === shopConfig?.loginToBuyLabel &&
      redirectToLoginPage(shopConfig?.loginPageURL)
    ) {
      return
    }
    if (addToCartButtonLabel === shopConfig?.notifyMeCTALabel) {
      setFormSubmitStatus('')
      setDialogNotifyMeIsOpen(true)
    }
    if (
      addToCartButtonLabel === shopConfig?.addToCartLabel ||
      addToCartButtonLabel === shopConfig?.preOrderCTALabel
    ) {
      handleAddToCart({
        ...getAddToCartMethodConfig()
      })
    }
  }, [
    productData,
    addToCartButtonLabel,
    shopConfig?.notifyMeCTALabel,
    shopConfig?.addToCartLabel,
    shopConfig?.preOrderCTALabel,
    shopConfig?.loginToBuyLabel,
    shopConfig?.loginPageURL,
    getAddToCartMethodConfig
  ])

  const addToCartProperties = useMemo(
    () => ({
      buttonProps: {
        onClick: onATCButtonClick,
        ...(addToCartButtonLabel ? { label: addToCartButtonLabel } : {}),
        loading: isButtonLoading,
        disable:
          addToCartButtonLabel === shopConfig?.outOfStockCTALabel ||
          isButtonDisabled
      },
      ...(isPreorder && preOrderMessage ? { preOrderMessage } : {}),
      qtySelectorProps: {
        ...(isQTYSelectorVisible &&
        addToCartButtonLabel === shopConfig?.addToCartLabel
          ? { orderQTY: orderQTYValues }
          : {}),
        defaultQTYValue:
          orderQTYValues?.[0]?.value ?? productQuantity?.toString() ?? '1',
        'aria-label': quantityLabel
      },
      onQTYChange: onAddToCartQuantityChange
    }),
    [
      onATCButtonClick,
      addToCartButtonLabel,
      isButtonLoading,
      isButtonDisabled,
      shopConfig?.outOfStockCTALabel,
      shopConfig?.addToCartLabel,
      isPreorder,
      preOrderMessage,
      isQTYSelectorVisible,
      orderQTYValues,
      productQuantity,
      quantityLabel,
      onAddToCartQuantityChange
    ]
  )

  const addToCartAlternativeContent = addToCart?.xfAddToCartContent
    ? {
        model: {
          xfPath: `${addToCart?.xfForAddToCart}`,
          xfContent: { ...addToCart?.xfAddToCartContent }
        }
      }
    : null

  const addToCartElement = useMemo(
    () =>
      shouldShowAddToCart ? (
        <AddToCart key={productID} {...addToCartProperties} />
      ) : null,
    [shouldShowAddToCart, addToCartProperties, productID]
  )

  const uspXFContent = useMemo(
    () =>
      general?.productUSPs
        ? {
            model: {
              xfPath: `${general?.productUSPs}`,
              xfContent: { ...general?.productUSPsXFContent }
            }
          }
        : null,
    [general]
  )

  const highlightsContent = useMemo(
    () =>
      uspXFContent ? <ExperienceFragmentContent {...uspXFContent} /> : null,
    [uspXFContent]
  )

  // Visibility controls
  const showDescription =
    general?.descriptionVisibility !== false && productDescription
  const showPrices =
    general?.pricesVisibility !== false &&
    priceInfoConfig?.currentPriceTextProps
  const shouldShowColorSelector =
    general?.colorDotsVisibility !== false &&
    (colorSelectorProperties?.colorVariants?.length ?? 0) > 1

  const configureAndBuyProps = {
    ...(currentProductVariant?.badges?.length
      ? {
          productChips: {
            chipPropsList: currentProductVariant?.badges?.map(
              (badge: {
                badgeDescription?: string
                fontColor?: { hexCode?: string }
                backgroundColor?: { hexCode?: string }
              }) => ({
                label: badge.badgeDescription,
                color: badge.fontColor?.hexCode,
                background: badge.backgroundColor?.hexCode
              })
            )
          }
        }
      : {}),
    ...(currentProductVariant?.globalArticleName
      ? {
          productHeader: {
            productName: currentProductVariant?.globalArticleName,
            ...(currentProductVariant?.nuance?.name
              ? {
                  description: currentProductVariant?.nuance?.name
                }
              : {})
          }
        }
      : {}),
    ...(shouldShowColorSelector
      ? {
          colorSelector: { ...colorSelectorProperties }
        }
      : {}),
    ...(showStockInfoLabel
      ? {
          stockInfo: {
            stockLabel: stockInfoLabel,
            ...(getStockStatus(effectiveStockLevelStatus)
              ? {
                  stockStatus:
                    !isSsr && getStockStatus(effectiveStockLevelStatus)
                }
              : {}),
            showStockMessage: showStockInfoLabel
          }
        }
      : {}),
    ...(showFlavourProfiles
      ? {
          flavourProfiles: {
            profiles: getIntensityGauge(
              currentProductVariant?.intensityMeter,
              shopConfig?.intensityLabel
            )
          } as FlavourProfilesProps
        }
      : {}),
    ...(attributesSelectorProps?.length && shouldShowAttributeSelector
      ? {
          attributeSelectors: [
            ...attributesSelectorProps,
            ...(multibuyAttributeSelectorProps
              ? [multibuyAttributeSelectorProps]
              : [])
          ]
        }
      : {}),
    ...((!attributesSelectorProps?.length || !shouldShowAttributeSelector) &&
    multibuyAttributeSelectorProps
      ? { attributeSelectors: [multibuyAttributeSelectorProps] }
      : {}),
    ...(addToCartElement
      ? {
          addToCart: addToCartElement
        }
      : null),
    ...(addToCartAlternativeContent
      ? {
          addToCartAlternativeContent: (
            <ExperienceFragmentContent {...addToCartAlternativeContent} />
          )
        }
      : {}),
    ...(ratesAndReviews?.enabled
      ? {
          rating: {
            productId: productID,
            type: RatingAndReviewsTypes.RatingSummary
          }
        }
      : {}),
    ...(shouldShowAddToCart
      ? {
          stickyCTA: {
            id: mobileStickyCtaId,
            ...(currentProductVariant?.globalArticleName
              ? {
                  productName: currentProductVariant?.globalArticleName
                }
              : {}),
            onButtonClick: onATCButtonClick,
            loadingButton: isButtonLoading,
            disabledButton:
              addToCartButtonLabel === shopConfig?.outOfStockCTALabel ||
              isButtonDisabled,
            buttonLabel: addToCartButtonLabel,
            colorName,
            onColorNameClick: onStickyColorNameClick,
            ...(isMultibuyActive || showPrices === false ? {} : priceInfoProps),
            ...(getStockStatus(currentProductVariantStockLevelStatus)
              ? {
                  productStatus: getStockStatus(
                    currentProductVariantStockLevelStatus
                  )
                }
              : {}),
            ...(shopConfig?.stickyAddToCartHidingObservedIDs?.length
              ? {
                  observableIDs: shopConfig?.stickyAddToCartHidingObservedIDs
                }
              : {}),
            ...(multibuySelectProps
              ? { multibuySelectProps }
              : { multibuySelectProps: undefined }),
            onStickyDisplayChange: onStickyAddToCartDisplayChange
          }
        }
      : {}),
    deliveryInfo: deliveryInfoMessage,
    backToShop: {
      label: general?.backToPLPLabel,
      linkUrl: general?.backToPLPUrl
    },
    ...(highlightsContent ? { highlightsContent } : {}),
    ...(general?.background ? { background: general?.background } : {}),
    ...(showDescription
      ? {
          productInfo: {
            description: productDescription
          }
        }
      : {}),
    ...(productImageGalleryProps?.length
      ? {
          productImageGallery: {
            gallery: productImageGalleryProps,
            zoomLabel: shopConfig?.doubleClickToZoomLabel,
            accessibilityProps: {
              dialogCloseButtonAriaLabel: shopConfig?.closeFullScreenAriaLabel,
              videoPosterPlayButtonAriaLabel:
                shopConfig?.productGalleryPlayButtonAriaLabel,
              zoomButtonAriaLabel: shopConfig?.openFullScreenAriaLabel,
              sliderAccessibilityProps: {
                nextSlideMessage: shopConfig?.productGalleryNextSlideAriaLabel,
                prevSlideMessage:
                  shopConfig?.productGalleryPreviousSlideAriaLabel,
                lastSlideMessage: shopConfig?.productGalleryLastSlideAriaLabel,
                firstSlideMessage: shopConfig?.productGalleryFirstSlideAriaLabel
              }
            }
          }
        }
      : {}),
    ...(showPrices
      ? {
          priceInfo: {
            ...priceInfoTextProps,
            currentPriceTextProps: {
              ...priceInfoTextProps.currentPriceTextProps,
              content:
                multibuyCurrentPrice ??
                priceInfoTextProps.currentPriceTextProps?.content ??
                undefined
            },
            originalPriceTextProps:
              isMultibuyActive || !priceInfoTextProps.originalPriceTextProps
                ? undefined
                : {
                    ...priceInfoTextProps.originalPriceTextProps,
                    content:
                      priceInfoTextProps.originalPriceTextProps.content ||
                      undefined
                  },
            ...(currentProductVariant?.price?.priceDiscount
              ?.discountValueDescription && !isMultibuyActive
              ? {
                  message:
                    currentProductVariant?.price?.priceDiscount
                      ?.discountValueDescription
                }
              : {})
          }
        }
      : {}),
    ...(multibuyEditorialDescription || editorialDescription
      ? {
          editorialDescription:
            multibuyEditorialDescription ?? editorialDescription
        }
      : {})
  }

  const configCartErrorMessageDialog = {
    errorMessageContent,
    dialogErrorMessageIsOpen,
    setDialogErrorMessageIsOpen
  }

  const cNotifyMeDialog = setDialogConfig(
    dialogNotifyMeIsOpen,
    setDialogNotifyMeIsOpen,
    shopConfig?.notifyMeDialogTitle ?? '',
    shopConfig?.closeAriaLabel ?? '',
    () => fetchContainerContent(shopConfig?.notifyMeXF, setNotifyMeContent)
  )

  useEffect(() => {
    if (addToCartButtonLabel === shopConfig?.notifyMeCTALabel) {
      fetchContainerContent(shopConfig?.notifyMeXF, setNotifyMeContent)
    }
  }, [addToCartButtonLabel])

  return (
    currentProductVariant && (
      <EditableComponent {...editableProps}>
        <CAndB {...(configureAndBuyProps as ConfigureAndBuyProps)} />
        <MiniCart {...configMiniCart} />
        <CartErrorMessageDialog {...configCartErrorMessageDialog} />
        <UIFoundationsDialog {...cNotifyMeDialog}>
          <Container model={{ ...notifyMeContent }} />
        </UIFoundationsDialog>
      </EditableComponent>
    )
  )
})

// left static function for emulating getServerProps
const getServerSideProps = async (props: any) => {
  const { key: productKey, config /* general */ } = props

  const fetchedData = await fetchProductData({
    config: config?.cbxConfig ?? config,
    key: productKey ?? '',
    includeVariants: 'true',
    includeRelationships: 'true',
    includeOffline: 'false',
    includeCrosslinked: 'false',
    ...getServerSideHeaders()
  })

  return {
    ...props,
    initialProductData: fetchedData || {}
  }
}

const getHeadData = async (props: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialProductData?: any
  general?: { disableMainImagePreload: boolean }
  pageUrl?: string
}): Promise<GetCAndBHeadDataReturnType> => {
  const {
    initialProductData,
    general: { disableMainImagePreload } = {},
    pageUrl
  } = props || {}

  const primaryImageUrl = getAssetImageUrl(initialProductData?.assets?.[0])

  return {
    status: initialProductData?.status || 200,
    seoTitle: initialProductData?.seo?.productsSeoTitle,
    dynamicPageTitle: initialProductData?.globalArticleName,
    seoDescription: initialProductData?.seo?.productsSeoDescription,
    assetFullUrl: primaryImageUrl
      ? getOptimizedImageExtension(primaryImageUrl)
      : undefined,
    headTags: disableMainImagePreload ? [] : headTagsConfig(initialProductData),
    jsonLD: generateProductJsonLD(initialProductData, pageUrl)
  }
}

const ConfigureAndBuy = Object.assign(ConfigureAndBuyComponent, {
  subscribeToGcodeSetup,
  unsubscribeFromGcodeSetup,
  getServerSideProps,
  getHeadData,
  setupGcode: undefined,
  gcodeSetupSubscribers: {}
}) as React.FC<EditableComponentPropsType<ConfigureAndBuyModelProps>> &
  ConfigureAndBuyStaticModelProps & {
    setupGcode: string | undefined
    gcodeSetupSubscribers: Record<string, { action: (gcode?: string) => void }>
  }

// Make setupGcode accessible via ConfigureAndBuy
Object.defineProperty(ConfigureAndBuy, 'setupGcode', {
  get: () => setupGcode,
  set: (value: string | undefined) => {
    setupGcode = value
  },
  enumerable: true,
  configurable: true
})

// Make gcodeSetupSubscribers accessible via ConfigureAndBuy
Object.defineProperty(ConfigureAndBuy, 'gcodeSetupSubscribers', {
  get: () => gcodeSetupSubscribers,
  enumerable: true,
  configurable: true
})

const ConfigureAndBuyWithErrorBoundary = withErrorBoundary(ConfigureAndBuy, {
  componentName: 'ConfigureAndBuy',
  fallback: <ErrorFallback componentName="ConfigureAndBuy" />
})

export default ConfigureAndBuyWithErrorBoundary
export { ConfigureAndBuy }
